import * as APIRoutes from '@mm/api-routes';
import { ObjectUtils } from '@mm/utils';
import { MyLeadsCreateAudienceRequestParams } from '../interfaces/myLeads';
import { AudiencesPaginationModel } from '../model/AudiencePaginationModel';
import AudiencesModel, { AudienceType } from '../model/AudiencesModel';
import { VariableOptionsModel } from '../model/VariableOptionsModel';
import AuthenticatedController from './AuthenticatedController';
import { DEFAULT_HEADERS } from './Controller';
import { snake } from '../helpers/converterString';
import _ from 'lodash';

export default class AudiencesController extends AuthenticatedController {
  static async getAll(
    botId: number,
    page = 1,
    perPage = 25,
    query?: string,
    withoutSystem?: boolean,
    sortColumn?: 'created_at' | 'contacts_count',
    sortDirection?: 'asc' | 'desc',
    inUse?: boolean,
    type?: AudienceType,
  ): Promise<AudiencesPaginationModel> {
    return super.get(
      APIRoutes.AUDIENCES({
        botId,
        page,
        perPage,
        query,
        withoutSystem,
        sortColumn,
        sortDirection,
        inUse,
        type,
      }),
    );
  }

  static async getAudienceById(
    botId: number,
    audienceId: number,
    params?: { inUse?: boolean },
  ): Promise<AudiencesModel> {
    const query = _.pickBy(snake(params), _.identity);
    return super.get(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}`,
      query,
    );
  }

  static async getAllCSV(botId: number, audienceId: number) {
    return super.get(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/contacts_csv`,
    );
  }

  static async createScheduledExport(
    botId: number,
    audienceId: number,
    params?: {
      emailAddress?: string;
      googleSheetId?: number | string;
      googleSheetTitle?: string;
      googleSpreadsheetId?: string;
      googleSpreadsheetTitle?: string;
      googleAccessTokenId?: number;
      fieldsForExport?: string[];
    },
  ) {
    return super.post(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/scheduled_exports`,
      { ...params },
    );
  }

  static async getContactsById({
    botId,
    audienceId,
    facebookPageId,
    pageNumber = 1,
    pageSize = 999999,
    query = '',
    withDeliverable = false,
    sortColumn,
    sortDirection,
  }: {
    botId: number;
    audienceId: number;
    facebookPageId: number;
    pageNumber?: number;
    pageSize?: number;
    query?: string;
    withDeliverable?: boolean;
    sortColumn?: 'last_session' | 'created_at';
    sortDirection?: 'asc' | 'desc';
  }) {
    const routeQuery = APIRoutes.AUDIENCE_CONTACTS({
      botId,
      audienceId,
      pageNumber,
      pageSize,
      facebookPageId,
      query,
      withDeliverable,
      sortColumn,
      sortDirection,
    });
    return super.get(routeQuery);
  }

  static async postAudience(
    params: MyLeadsCreateAudienceRequestParams,
    botId: number,
  ): Promise<AudiencesModel> {
    const { esFilters, filterParams, tagsFilters, ...restParams } =
      params || {};
    const snakedAudience = snake(restParams);

    const updatedAudience = {
      ...snakedAudience,
      ...(Object.keys(params.filterParams || {}).length && {
        filter_params: snake(filterParams),
      }),
      ...(Object.keys(params.tagsFilters || {}).length && {
        tags_filters: snake(tagsFilters),
      }),
      ...(Object.keys(esFilters || {}).length && {
        es_filters: { query: ObjectUtils.deepSnakeKeys(esFilters || {}, true) },
      }),
    };

    const response = await super.post(
      `${APIRoutes.BOTS}/${botId}/audiences`,
      updatedAudience,
      null,
      DEFAULT_HEADERS,
      null,
      false,
      true,
    );
    return response.audience;
  }

  static async putAudience(
    updatedAudience: Partial<AudiencesModel>,
    botId: number,
  ) {
    const { esFilters, filterParams, tagsFilters, ...restParams } =
      updatedAudience || {};
    const snakedAudience = snake(restParams);

    const audienceParams = {
      ...snakedAudience,
      ...(Object.keys(filterParams || {}).length && {
        filter_params: snake(filterParams),
      }),
      ...(Object.keys(tagsFilters || {}).length && {
        tags_filters: snake(tagsFilters),
      }),
      ...(Object.keys(esFilters || {}).length && {
        es_filters: { query: ObjectUtils.deepSnakeKeys(esFilters || {}, true) },
      }),
    };

    return super.put(
      `${APIRoutes.BOTS}/${botId}/audiences/${updatedAudience.id}`,
      audienceParams,
      null,
      DEFAULT_HEADERS,
      true,
    );
  }

  static async deleteContacts(
    botId: number,
    audienceId: number,
    deleteContacts: boolean,
  ) {
    return super.post(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/delete_contacts`,
      { deleteContacts },
    );
  }

  static async getVariables(botId: number) {
    const response = await super.get(
      `${APIRoutes.BOTS}/${botId}/audiences/variables`,
    );
    return response.variables;
  }

  static async getVariableOptions(
    botId: number,
    variableId: number,
    variableType: string,
    variableName: string,
    withOptions?: boolean,
  ) {
    const url = `${APIRoutes.BOTS}/${botId}/audiences/variable_options`;
    const options = `variable_id=${variableId}&variable_type=${variableType}&variable_name=${variableName}&with_options=${withOptions}`;

    return (await super.get(`${url}?${options}`)) as VariableOptionsModel;
  }

  static async deleteAudience(audienceId: number, botId: number) {
    return super.delete(`${APIRoutes.BOTS}/${botId}/audiences/${audienceId}`);
  }

  static async cloneAudience(audienceId: number, botId: number) {
    return super.post(
      `${APIRoutes.BOTS}/${botId}/audiences/${audienceId}/clone`,
    );
  }
}
